/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'

// Chakra Components
import {Box, Flex, Grid, GridItem, Stack, Text} from '@chakra-ui/react'

// Project Components
import ItemVariantProvider from '../item-variant'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import CartItemVariantPrice from '../item-variant/item-price'
import LoadingSpinner from '../loading-spinner'
import {FormattedMessage} from 'react-intl'
/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} product Product to be represented in the list item.
 * @param {node} primaryAction Child component representing the most prominent action to be performed by the user.
 * @param {node} secondaryActions Child component representing the other actions relevant to the product to be performed by the user.
 * @param {func} onItemQuantityChange callback function to be invoked whenever item quantity changes.
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const ProductItem = ({product, productLineItem, secondaryActions, showLoading = false}) => {
    return (
        <Box position="relative" data-testid={`sf-cart-item-${product.productId}`}>
            <ItemVariantProvider variant={product}>
                {showLoading && <LoadingSpinner />}
                <Box
                    backgroundColor="stokkeCore.white"
                    paddingX={{base: 4, lg: 6}}
                    paddingY={{base: 6}}
                >
                    <Grid
                        templateColumns={{base: '1fr auto', lg: 'auto 1fr'}}
                        templateRows={{base: 'auto auto'}}
                        templateAreas={{
                            base: '"infos img" "bottom bottom"',
                            lg: '"img infos" "img bottom"'
                        }}
                        columnGap={{base: 6, lg: 10}}
                        rowGap={{base: 4, lg: 6}}
                    >
                        <GridItem gridArea="img">
                            <CartItemVariantImage
                                width={{base: '90px', lg: '160px', xl: '240px'}}
                                link={product['c_product-type'] !== 'Engraving'}
                            />
                        </GridItem>

                        <GridItem gridArea="infos">
                            <Stack spacing={4}>
                                <CartItemVariantName
                                    link={product['c_product-type'] !== 'Engraving'}
                                />
                                {product.c_incompatibleProductNames?.length > 0 && (
                                    <Box>
                                        <Text
                                            textStyle="Semi Bold Small"
                                            color="stokkeFunctional.alertRed"
                                            as="span"
                                            mr={1}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Warning:"
                                                id="cart.info.incompatible_product_title"
                                            />
                                        </Text>
                                        <Text
                                            textStyle="Regular Small"
                                            color="stokkeFunctional.alertRed"
                                            as="span"
                                        >
                                            <FormattedMessage
                                                defaultMessage="{masterProductName} is not compatible with {incompatibleProductName}."
                                                id="cart.info.incompatible_product_text"
                                                values={{
                                                    masterProductName:
                                                        product.c_incompatibleProductNames[0],
                                                    incompatibleProductName: product.name
                                                }}
                                            />
                                        </Text>
                                    </Box>
                                )}
                                <CartItemVariantAttributes />
                            </Stack>
                        </GridItem>

                        <GridItem gridArea="bottom" alignSelf="flex-end">
                            <Flex justifyContent="space-between" alignItems="flex-end" gap={4}>
                                {secondaryActions}
                                <CartItemVariantPrice productLineItem={productLineItem} />
                            </Flex>
                        </GridItem>
                    </Grid>
                </Box>
            </ItemVariantProvider>
        </Box>
    )
}

ProductItem.propTypes = {
    product: PropTypes.object,
    productLineItem: PropTypes.object,
    showLoading: PropTypes.bool,
    secondaryActions: PropTypes.node
}

export default ProductItem
